import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { getFileUrl, postcardApi } from '../services/api';
import { Postcard } from '../types/postcard';

export const PostcardDetails: React.FC = () => {
  const { postcardId } = useParams<{ postcardId: string }>();
  const navigate = useNavigate();
  const [postcard, setPostcard] = useState<Postcard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPostcard = async () => {
      try {
        const response = await postcardApi.getById(postcardId!);
        setPostcard(response.data);
      } catch (err) {
        setError('Failed to load postcard details');
        console.error('Error fetching postcard:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPostcard();
  }, [postcardId]);

  const handleSendClick = () => {
    navigate(`/checkout/${postcardId}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !postcard) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography color="error" align="center">
          {error || 'Postcard not found'}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
      <Grid container spacing={4}>
        {/* Image Section */}
        <Grid item xs={12} md={7}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: 'background.paper',
            }}
          >
            <img
              src={getFileUrl(postcard.imageUrl)}
              alt="Generated postcard"
              style={{
                width: '100%',
                maxHeight: '600px',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
          </Paper>
        </Grid>

        {/* Details Section */}
        <Grid item xs={12} md={5}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="h4" gutterBottom>
              Your Postcard
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Created on {new Date(postcard.createdAt).toLocaleDateString()}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<ImageIcon />}
                onClick={() => navigate(`/templates/${postcard.templateId}`)}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                View Template Details
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            {postcard.referenceImageUrl && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImageIcon color="primary" />
                    Reference Image
                  </Typography>
                  <img
                    src={getFileUrl(postcard.referenceImageUrl)}
                    alt="Reference image"
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                    }}
                  />
                </Box>
                <Divider sx={{ my: 3 }} />
              </>
            )}

            <Box sx={{ mt: 'auto', pt: 3 }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<SendIcon />}
                onClick={handleSendClick}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                SEND IT
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}; 